import { unitLabel, warrantyUnitValue } from '@/lib/orders';

import type { WarrantyEligibleItem } from './types';

/** The three-part Unit codec lives in `@/lib/orders` (ADR-0024) so `device-params` can share it
 *  without closing an `orders` cycle. Re-exported here because this submodule's call sites import it
 *  from `./format`. */
export { parseWarrantyUnitValue, warrantyUnitValue } from '@/lib/orders';

/**
 * One option per eligible Unit, in response order. `eligibleUnits` decides which options exist and
 * carries the original numbers, so excluded Units leave gaps rather than re-sequencing the rest —
 * "Hydro Pump 7" means the same physical article here as in tracking and installation.
 *
 * A Bundle needs no special case: the endpoint returns one entry **per component device** and never
 * the Bundle itself, so "the Bundle is not a selectable option" is already true without any
 * frontend filtering (ADR-0023). A component's label is the shared Unit label plus its parent
 * Bundle, AC-verbatim.
 */
export function toDeviceOptions(
  items: WarrantyEligibleItem[],
): Array<{ value: string; label: string }> {
  return items.flatMap((item) =>
    item.eligibleUnits.map((unitIndex) => {
      const name = unitLabel(item.productName, item.quantity, unitIndex);
      return {
        value: warrantyUnitValue(item.orderItemId, unitIndex, item.bundleDeviceId),
        label: item.bundleName ? `${name} (from ${item.bundleName})` : name,
      };
    }),
  );
}
